import { useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { progressOriginBack } from '@/lib/course-admin-navigation';

interface ProgressItem {
  id: string;
  percent?: number;
  status?: string;
  timeSpent?: number;
  lastAccessedAt?: string;
  sectionItem?: { id: string; title: string; sectionId?: string };
}

interface CourseDetail {
  id: string;
  name: string;
  sections?: { id: string; title: string; items: { id: string; title: string }[] }[];
}

interface UserDetail {
  id: string;
  name: string;
  fullName?: string;
  email: string;
  avatarUrl?: string;
}

const STATUS_LABELS: Record<string, string> = {
  not_started: 'No iniciado',
  in_progress: 'En progreso',
  completed: 'Completado',
  approved: 'Aprobado',
  failed: 'Reprobado',
  pending: 'Pendiente',
  blocked: 'Bloqueado',
};

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

function formatTime(seconds?: number): string {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function statusPillClass(status?: string): string {
  switch (status) {
    case 'not_started':
      return 'lms-progress-detail__status-pill--not-started';
    case 'in_progress':
      return 'lms-progress-detail__status-pill--in-progress';
    case 'completed':
    case 'approved':
      return 'lms-progress-detail__status-pill--completed';
    case 'failed':
      return 'lms-progress-detail__status-pill--failed';
    case 'pending':
    case 'blocked':
      return 'lms-progress-detail__status-pill--pending';
    default:
      return 'lms-progress-detail__status-pill--not-started';
  }
}

function itemStatusClass(status?: string): string {
  if (status === 'completed' || status === 'approved') return 'lms-progress-detail__item-status--done';
  if (status === 'in_progress') return 'lms-progress-detail__item-status--active';
  if (status === 'failed') return 'lms-progress-detail__item-status--failed';
  return '';
}

export function ProgressShowPage() {
  const { userId, courseId } = useParams<{ userId: string; courseId: string }>();
  const [searchParams] = useSearchParams();
  const back = progressOriginBack(searchParams, courseId, userId);

  const { data: items, isLoading, error } = useQuery({
    queryKey: ['progress', userId, courseId],
    queryFn: () => api<ProgressItem[]>(`/progress/courses/${courseId}/users/${userId}`),
    enabled: Boolean(userId && courseId),
  });

  const { data: course } = useQuery({
    queryKey: ['courses', courseId],
    queryFn: () => api<CourseDetail>(`/courses/${courseId}`),
    enabled: Boolean(courseId),
  });

  const { data: student } = useQuery({
    queryKey: ['users', userId],
    queryFn: () => api<UserDetail>(`/users/${userId}`),
    enabled: Boolean(userId),
  });

  const progressMap = useMemo(() => {
    const map = new Map<string, ProgressItem>();
    for (const item of items ?? []) {
      if (item.sectionItem?.id) map.set(item.sectionItem.id, item);
    }
    return map;
  }, [items]);

  const sections = course?.sections ?? [];
  const allItems = items ?? [];
  const completed = allItems.filter(
    (i) => i.status === 'completed' || i.status === 'approved' || (i.percent ?? 0) >= 100,
  ).length;
  const total = sections.reduce((acc, s) => acc + s.items.length, 0) || allItems.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  const timeSpent = allItems.reduce((acc, i) => acc + (i.timeSpent ?? 0), 0);
  const overviewStatus =
    percent >= 100 ? 'completed' : completed > 0 || allItems.some((i) => i.status === 'in_progress') ? 'in_progress' : 'not_started';

  const studentName = student?.fullName?.trim() || student?.name || 'Estudiante';

  return (
    <div className="lms-progress-detail lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to={back.to} label={back.label} />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Reporte de Progreso Detallado</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {studentName} · {course?.name ?? 'Curso'}
          </p>
        </div>
      </header>

      {error && (
        <p className="mb-4 text-sm text-danger-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          <section className="lms-progress-detail__profile">
            <div className="lms-progress-detail__profile-info">
              {student?.avatarUrl ? (
                <img src={student.avatarUrl} alt="" className="lms-progress-detail__avatar-img" />
              ) : (
                <span className="lms-progress-detail__avatar" aria-hidden="true">
                  {initials(studentName) || '?'}
                </span>
              )}

              <div className="lms-progress-detail__profile-meta">
                <h2 className="lms-progress-detail__name">{studentName}</h2>
                <div className="lms-progress-detail__email">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    mail
                  </span>
                  <span>{student?.email ?? '—'}</span>
                </div>
                <span className={`lms-progress-detail__status-pill ${statusPillClass(overviewStatus)}`}>
                  <span className="lms-progress-detail__status-dot" aria-hidden="true" />
                  {STATUS_LABELS[overviewStatus] ?? overviewStatus}
                </span>
              </div>
            </div>

            <div className="lms-progress-detail__metrics">
              <div className="lms-progress-detail__metrics-top">
                <span>Progreso del curso</span>
                <strong>{percent}%</strong>
              </div>
              <div
                className="lms-progress-detail__bar"
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="lms-progress-detail__bar-fill" style={{ width: `${percent}%` }} />
              </div>
              <div className="lms-progress-detail__metrics-rows">
                <div>
                  <span>Objetos completados:</span>
                  <strong>
                    {completed} de {total}
                  </strong>
                </div>
                <div>
                  <span>Tiempo total invertido:</span>
                  <strong>{formatTime(timeSpent)}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="lms-progress-detail__modules">
            <h3 className="lms-progress-detail__modules-title">Desglose por Módulo</h3>

            {sections.length === 0 ? (
              <div className="lms-progress-detail__empty">
                <p className="text-sm text-dash-on-surface-variant">
                  Sin módulos — Este curso aún no tiene secciones de contenido.
                </p>
              </div>
            ) : (
              sections.map((section, sectionIndex) => {
                const sectionCompleted = section.items.filter((item) => {
                  const progress = progressMap.get(item.id);
                  const status = progress?.status;
                  return status === 'completed' || status === 'approved' || (progress?.percent ?? 0) >= 100;
                }).length;

                return (
                  <article key={section.id} className="lms-progress-detail__module">
                    <div className="lms-progress-detail__module-head">
                      <h4>
                        {sectionIndex + 1}. {section.title}
                      </h4>
                      <span className="lms-progress-detail__module-count">
                        {sectionCompleted}/{section.items.length} completados
                      </span>
                    </div>

                    <div className="lms-progress-detail__table-scroll">
                      <table className="lms-progress-detail__table">
                        <thead>
                          <tr>
                            <th className="lms-progress-detail__th--object">Objeto de Aprendizaje</th>
                            <th>Tipo</th>
                            <th>Estado</th>
                            <th className="lms-progress-detail__th--access">Último acceso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.items.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="lms-progress-detail__empty-row">
                                No hay objetos en este módulo.
                              </td>
                            </tr>
                          ) : (
                            section.items.map((item) => {
                              const progress = progressMap.get(item.id);
                              const status = progress?.status ?? 'not_started';
                              const itemPercent = progress?.percent ?? 0;
                              const lastAccess = progress?.lastAccessedAt
                                ? new Date(progress.lastAccessedAt).toLocaleString('es-ES', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : '—';

                              return (
                                <tr key={item.id}>
                                  <td>
                                    <div className="lms-progress-detail__object">
                                      <span className="lms-progress-detail__object-icon lms-progress-detail__object-icon--text">
                                        <span className="material-symbols-outlined" aria-hidden="true">
                                          description
                                        </span>
                                      </span>
                                      <span className="lms-progress-detail__object-name">{item.title}</span>
                                    </div>
                                  </td>
                                  <td className="lms-progress-detail__type">—</td>
                                  <td>
                                    <div className={`lms-progress-detail__item-status ${itemStatusClass(status)}`}>
                                      <span className="lms-progress-detail__item-dot" aria-hidden="true" />
                                      <span>
                                        {STATUS_LABELS[status] ?? status} ({Math.round(itemPercent)}%)
                                      </span>
                                    </div>
                                  </td>
                                  <td className="lms-progress-detail__access">{lastAccess}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </article>
                );
              })
            )}
          </section>
        </>
      )}
    </div>
  );
}
